import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { dashboardApi, vaultApi, reportsApi } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import VaultCylinder from "@/components/shared/VaultCylinder";
import VaultDetailDrawer from "@/features/dashboard/VaultDetailDrawer";
import SentinelInsightsPanel from "@/features/dashboard/SentinelInsightsPanel";
import SentinelScoreStrip from "@/features/dashboard/SentinelScoreStrip";
import MultiCurrencyVaultStrip from "@/features/dashboard/MultiCurrencyVaultStrip";
import GaugeMeter from "@/components/shared/GaugeMeter";
import { ExplainButton, ExplainPanel, explainVault } from "@/components/shared/ExplainPanel";
import PoolWidget from "@/components/shared/PoolWidget";
import OnboardingOverlay, { useOnboardingState } from "@/components/shared/OnboardingOverlay";
import AdditionalVaultsPanel from "@/components/shared/AdditionalVaultsPanel";
import DemoDataBadge from "@/components/shared/DemoDataBadge";
import {
  Gem, ShieldCheck, Lock, Link2, AlertTriangle, ArrowRight, BrainCircuit,
  TrendingUp, Eye, Shield, Zap, Activity, Scale, ChevronDown, ChevronUp,
  RefreshCw, Search, Target, PenLine, History, Loader2, Layers, FileText, Download,
  CreditCard, Landmark, PiggyBank, Rocket, HelpCircle
} from "lucide-react";

const SEVERITY_STYLES = {
  critical: { border: "border-red-500/30", bg: "bg-red-500/[0.06]", text: "text-red-400", badge: "bg-red-500/15 text-red-400" },
  high: { border: "border-amber-500/30", bg: "bg-amber-500/[0.06]", text: "text-amber-400", badge: "bg-amber-500/15 text-amber-400" },
  medium: { border: "border-slate-500/30", bg: "bg-slate-500/[0.06]", text: "text-slate-400", badge: "bg-slate-500/15 text-slate-400" },
};

export default function DashboardPage() {
  const { formatMoney, i18n, token, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reality, setReality] = useState(null);
  const [loading, setLoading] = useState(true);
  const [explainData, setExplainData] = useState(null);
  const [explainOpen, setExplainOpen] = useState(false);
  const [showVaultDetail, setShowVaultDetail] = useState(false);
  // Onboarding state is owned by the backend now. ``shouldShow`` is the
  // ONLY signal we trust for auto-display. The Guide button calls
  // ``start()`` to manually re-open the tour.
  const onboarding = useOnboardingState({
    token,
    user,
    // Tour is dashboard-only; admin/owner pages never mount this hook
    // because DashboardPage isn't rendered there. ``paused`` covers
    // any future case where the dashboard is embedded in an admin view.
    paused: false,
  });
  const [tourOpen, setTourOpen] = useState(false);
  useEffect(() => {
    // Auto-open once the API confirms we should — never from local state alone.
    if (!onboarding.loading && onboarding.shouldShow && !loading && data) {
      setTourOpen(true);
    }
  }, [onboarding.loading, onboarding.shouldShow, loading, data]);
  const [adjustVault, setAdjustVault] = useState(null);
  const [vaultDetailType, setVaultDetailType] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [adjusting, setAdjusting] = useState(false);
  const [ledgerVault, setLedgerVault] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [truthReport, setTruthReport] = useState(null);

  useEffect(() => { loadData(); }, []);

  const [loadError, setLoadError] = useState(null);
  const loadData = async () => {
    setLoadError(null);
    // Hard 15s ceiling — the dashboard must NEVER hang forever, even if a
    // long AI Diagnostic is running in the background and the FastAPI
    // worker is momentarily slow. This guarantees the page renders the
    // last known truth (or the safe empty shape) within 15s.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    try {
      const [summaryRes, realityRes] = await Promise.all([
        dashboardApi.summary({ signal: controller.signal }).catch((e) => { console.error("summary failed", e); return null; }),
        dashboardApi.financialReality({ signal: controller.signal }).catch((e) => { console.error("financial-reality failed", e); return null; }),
      ]);
      if (summaryRes) setData(summaryRes.data);
      else setLoadError("summary_unavailable");
      if (realityRes) setReality(realityRes.data);
    } catch (e) {
      console.error(e);
      setLoadError("unexpected");
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const handleManualAdjust = async () => {
    if (!adjustVault || !adjustReason.trim() || !adjustAmount) return;
    setAdjusting(true);
    try {
      await vaultApi.manualAdjust({
        vault_type: adjustVault,
        amount: parseFloat(adjustAmount),
        reason: adjustReason,
      });
      setAdjustVault(null);
      setAdjustAmount("");
      setAdjustReason("");
      await loadData();
    } catch (e) { console.error(e); }
    finally { setAdjusting(false); }
  };

  const openLedger = async (vaultType) => {
    setLedgerVault(vaultType);
    setLedgerLoading(true);
    try {
      const res = await vaultApi.ledger(vaultType);
      setLedgerEntries(res.data.entries || []);
    } catch (e) { console.error(e); setLedgerEntries([]); }
    finally { setLedgerLoading(false); }
  };

  const generateTruthReport = async () => {
    setReportLoading(true);
    try {
      const res = await reportsApi.generateTruthReport();
      setTruthReport(res.data);
    } catch (e) { console.error(e); }
    finally { setReportLoading(false); }
  };

  const downloadReport = async (format) => {
    if (!truthReport?.report_id) return;
    try {
      const res = await reportsApi.downloadTruthReport(truthReport.report_id, format);
      const blob = new Blob([res.data], { type: format === "pdf" ? "application/pdf" : "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `financial-truth-report.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Safe defaults — the dashboard must render even if /dashboard/summary or
  // /dashboard/financial-reality errored. Diagnostic failure state must NEVER
  // poison these defaults.
  const safeData = data || {
    ownership: { owned: 0, custody: 0, restricted: 0, assigned: 0, total: 0 },
    vaults: [],
    next_obligation: null,
    days_to_next_obligation: null,
    behaviour_state: { collapsed_state: "STRATEGIC", state_probs: { SURVIVAL: 0.1, EMOTIONAL: 0.2, STRATEGIC: 0.7 } },
    probity_score: 0,
    coaching_preview: [],
    latest_bubble: null,
    obligations_summary: [],
    currency: "GBP",
  };
  const ownership = safeData.ownership || { owned: 0, custody: 0, restricted: 0, assigned: 0, total: 0 };
  const vaults = Array.isArray(safeData.vaults) ? safeData.vaults : [];
  const behaviour_state = safeData.behaviour_state || { collapsed_state: "STRATEGIC" };
  const probity_score = safeData.probity_score || 0;
  const coaching_preview = safeData.coaching_preview || [];
  const latest_bubble = safeData.latest_bubble || null;
  const obligations_summary = safeData.obligations_summary || [];
  const r = reality || {};
  const cur = r.reporting_currency || "GBP";
  // 6-vault foundation only. Expansion 4 (CREDIT_FACILITY_CUSTODY,
  // LOAN_OBLIGATION, RESERVE_CAPITAL, STRATEGIC_CAPITAL) are temporarily
  // hidden from the dashboard while the 6-vault system is stabilised in
  // production. Backend still supports all 10; flip the flag to re-enable.
  const ENABLE_EXPANSION_VAULTS = false;
  const vaultOrder = ENABLE_EXPANSION_VAULTS ? [
    "TAX_CUSTODY", "SUPPLIER_OBLIGATION", "CUSTOMER_TRUST",
    "INVENTORY_CUSTODY", "OPERATING_CUSTODY", "FREE_EQUITY",
    "CREDIT_FACILITY_CUSTODY", "LOAN_OBLIGATION",
    "RESERVE_CAPITAL", "STRATEGIC_CAPITAL",
  ] : [
    "TAX_CUSTODY", "SUPPLIER_OBLIGATION", "CUSTOMER_TRUST",
    "INVENTORY_CUSTODY", "OPERATING_CUSTODY", "FREE_EQUITY",
  ];
  const sortedVaults = vaultOrder.map(t => vaults.find(v => v.vault_type === t)).filter(Boolean);
  const illusionPct = r.cash_illusion_percentage || 0;
  const pva = r.perceived_vs_actual || {};
  const pressureLevel = r.pressure_level || "STABLE";
  const pressureColors = {
    CRITICAL: { bg: "bg-red-500/15", border: "border-red-500/30", text: "text-red-400", dot: "bg-red-500" },
    HIGH_RISK: { bg: "bg-amber-500/15", border: "border-amber-500/30", text: "text-amber-400", dot: "bg-amber-500" },
    MODERATE_RISK: { bg: "bg-yellow-500/10", border: "border-yellow-500/30", text: "text-yellow-400", dot: "bg-yellow-500" },
    STABLE: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-400", dot: "bg-emerald-500" },
  };
  const pc = pressureColors[pressureLevel] || pressureColors.STABLE;

  // Always-present narrative — so the dashboard banner renders even with no
  // applied truth yet. Diagnostic failure must never blank this out.
  const truthAvailable = r.truth_available !== false && (r.total_funds ?? 0) !== 0;
  const narrative = r.narrative || (truthAvailable
    ? null
    : {
        headline: "No applied financial truth yet.",
        subtext: "Upload and commit financial records from the Data Pool to build your truth layer. The dashboard reflects only committed, applied truth — never in-progress diagnostics.",
      });

  return (
    <div className="space-y-6" data-testid="dashboard-page">

      {/* Tour control — always-available, restartable */}
      <div className="flex items-center justify-end -mb-3">
        <button
          onClick={async () => { await onboarding.start(); setTourOpen(true); }}
          className="text-[11px] text-slate-500 hover:text-amber-400 inline-flex items-center gap-1.5 transition-colors"
          data-testid="onboarding-guide-btn"
          aria-label="Restart guided tour"
        >
          <HelpCircle className="w-3.5 h-3.5" /> Guide
        </button>
      </div>

      {loadError && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.05] px-4 py-3 text-xs text-amber-300/90" data-testid="dashboard-soft-error">
          We couldn't refresh all dashboard signals just now. Your last committed truth is still shown. This is never caused by a failed AI Diagnostic — those stay inside the Data Pool.
        </div>
      )}


      {/* ═══════════════════════════════════════════════════════
          SECTION 1 — FINANCIAL REALITY SUMMARY (TOP BANNER)
          ═══════════════════════════════════════════════════════ */}
      {narrative && (
        <div className="rounded-xl border border-cyan-500/15 bg-[#0a1628] p-6" data-testid="financial-reality-banner" data-tour="reality-banner">
          <div className="flex items-start gap-5">
            <div className="w-14 h-14 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center shrink-0">
              <BrainCircuit className="w-7 h-7 text-cyan-400" />
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-barlow font-bold text-slate-50 leading-snug flex items-center gap-3 flex-wrap" data-testid="reality-headline">
                {narrative.headline}
                <DemoDataBadge
                  demoMode={r?.demo_mode === true}
                  demoLabel={r?.demo_label || "Demo Data"}
                  dataSource={r?.data_source}
                />
              </h1>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed max-w-3xl" data-testid="reality-subtext">
                {narrative.subtext}
              </p>
              {/* Cash Illusion Indicator */}
              <div className="flex items-center gap-4 mt-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-28 h-2 rounded-full bg-slate-800 overflow-hidden">
                    <div className={`h-full rounded-full ${illusionPct > 60 ? "bg-red-500" : illusionPct > 40 ? "bg-amber-500" : "bg-emerald-500"}`}
                      style={{ width: `${Math.min(100, illusionPct)}%` }} />
                  </div>
                  <span className={`text-xs font-mono font-bold ${illusionPct > 60 ? "text-red-400" : illusionPct > 40 ? "text-amber-400" : "text-emerald-400"}`}>
                    {illusionPct}% obligated
                  </span>
                </div>
                <span className="text-[10px] text-slate-600">|</span>
                <span className="text-xs text-slate-500">
                  {cur} {(r.total_funds || 0).toLocaleString(undefined, {maximumFractionDigits: 0})} total
                </span>
                <span className="text-[10px] text-slate-600">|</span>
                <span className="text-xs text-emerald-400 font-semibold">
                  {cur} {(r.owned_capital || 0).toLocaleString(undefined, {maximumFractionDigits: 0})} truly yours
                </span>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* ═══════════════════════════════════════════════════════
          PERCEIVED vs ACTUAL + PRESSURE INDICATOR
          ═══════════════════════════════════════════════════════ */}
      {r.narrative && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          {/* Perceived vs Actual — impossible to miss */}
          <div className="lg:col-span-3 rounded-xl bg-slate-900/60 border border-slate-800/50 p-5" data-testid="perceived-vs-actual">
            <h3 className="text-[10px] text-slate-500 uppercase tracking-[0.2em] font-semibold mb-3">Perceived vs Actual</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">Perceived Balance</div>
                <div className="text-2xl font-mono tabular font-bold text-slate-200 mt-1">{formatMoney(pva.perceived_balance || r.total_funds || 0)}</div>
                <div className="text-[10px] text-slate-600 mt-0.5">What your bank shows</div>
              </div>
              <div>
                <div className="text-[10px] text-emerald-500/80 uppercase tracking-wider">Actually Owned</div>
                <div className="text-2xl font-mono tabular font-bold text-emerald-400 mt-1">{formatMoney(pva.actually_owned || r.owned_capital || 0)}</div>
                <div className="text-[10px] text-emerald-500/50 mt-0.5">What is truly yours</div>
              </div>
              <div>
                <div className="text-[10px] text-red-500/80 uppercase tracking-wider">Difference</div>
                <div className="text-2xl font-mono tabular font-bold text-red-400 mt-1">{formatMoney(pva.difference || r.obligated_funds || 0)}</div>
                <div className="text-[10px] text-red-500/50 mt-0.5">Belongs to others</div>
              </div>
            </div>
          </div>

          {/* Financial Pressure Indicator */}
          <div className={`rounded-xl border ${pc.border} ${pc.bg} p-5 flex flex-col justify-center`} data-testid="pressure-indicator">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-3 h-3 rounded-full ${pc.dot} ${pressureLevel === "CRITICAL" ? "animate-pulse" : ""}`} />
              <span className={`text-xs font-mono font-bold uppercase tracking-wider ${pc.text}`}>
                {pressureLevel.replace("_", " ")}
              </span>
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed">{r.pressure_description || ""}</p>
          </div>
        </div>
      )}


      {/* ═══════════════════════════════════════════════════════
          SECTION 2 — OWNERSHIP BREAKDOWN (WITH EXPLANATIONS)
          ═══════════════════════════════════════════════════════ */}
      <section data-testid="ownership-section" data-tour="ownership-breakdown">
        <h2 className="font-barlow font-semibold text-xs text-slate-500 uppercase tracking-[0.2em] mb-3">Ownership Breakdown</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { key: "owned", icon: Gem, color: "#10B981", borderColor: "border-l-emerald-500", alwaysShow: true },
            { key: "custody", icon: ShieldCheck, color: "#3B82F6", borderColor: "border-l-blue-500", alwaysShow: true },
            { key: "restricted", icon: Lock, color: "#F59E0B", borderColor: "border-l-amber-500", alwaysShow: true },
            { key: "assigned", icon: Link2, color: "#F43F5E", borderColor: "border-l-rose-500", alwaysShow: false },
            // Expansion-4 buckets — hidden while 6-vault is stabilised
            ...(ENABLE_EXPANSION_VAULTS ? [
              { key: "borrowed", icon: CreditCard, color: "#EC4899", borderColor: "border-l-pink-500", alwaysShow: false },
              { key: "reserved", icon: PiggyBank, color: "#14B8A6", borderColor: "border-l-teal-500", alwaysShow: false },
              { key: "strategic", icon: Rocket, color: "#FB923C", borderColor: "border-l-orange-500", alwaysShow: false },
            ] : []),
          ].filter(tile => {
            // Always show the 3 core buckets; only show new buckets when they have a balance
            if (tile.alwaysShow) return true;
            const ob = r.ownership_breakdown?.[tile.key] || {};
            return Math.abs(ob.amount || 0) > 0;
          }).map(tile => {
            const ob = r.ownership_breakdown?.[tile.key] || {};
            return (
              <div key={tile.key}
                className={`rounded-xl bg-slate-900/60 border border-slate-800/50 border-l-[3px] ${tile.borderColor} p-4`}
                data-testid={`ownership-${tile.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <tile.icon className="w-4 h-4" style={{ color: tile.color }} />
                  <span className="text-[11px] text-slate-500 uppercase tracking-wider font-manrope">{ob.label || tile.key}</span>
                </div>
                <div className="text-2xl font-mono tabular font-bold text-slate-50 mb-2">
                  {formatMoney(ob.amount || ownership[tile.key] || 0)}
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed">{ob.explanation || ""}</p>
                {ob.implication && (
                  <p className="text-[10px] text-slate-500 mt-1 italic">{ob.implication}</p>
                )}
              </div>
            );
          })}
        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════
          SECTION 3 — VAULT INTELLIGENCE
          ═══════════════════════════════════════════════════════ */}
      <section data-testid="vault-intelligence" data-tour="vault-intelligence">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-barlow font-semibold text-xs text-slate-500 uppercase tracking-[0.2em]">Vault Intelligence</h2>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowVaultDetail(!showVaultDetail)}
              className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 transition-colors" data-testid="toggle-vault-detail">
              {showVaultDetail ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {showVaultDetail ? "Hide Explanations" : "Show Explanations"}
            </button>
            <button onClick={() => navigate("/dashboard/vaults")} className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1 transition-colors">
              View All <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
        <Card className="bg-slate-900/40 border-slate-800/50">
          <CardContent className="p-4 md:p-6">
            <div className="grid grid-cols-3 lg:grid-cols-6 gap-4">
              {sortedVaults.map((vault, idx) => (
                <div
                  key={`${vault.vault_type || vault.id || idx}-${vault.currency || "native"}`}
                  className="flex flex-col items-center"
                >
                  <VaultCylinder vault={vault} formatMoney={formatMoney} onClick={(v) => setVaultDetailType(v.vault_type)} />
                  <div className="flex items-center gap-1 mt-2">
                    <button onClick={() => setVaultDetailType(vault.vault_type)}
                      className="text-[9px] text-cyan-400 hover:text-cyan-300 flex items-center gap-0.5 transition-colors"
                      data-testid={`vault-breakdown-${vault.vault_type}`}>
                      <Eye className="w-2.5 h-2.5" /> Breakdown
                    </button>
                    <span className="text-slate-700 text-[8px]">|</span>
                    <button onClick={() => { setAdjustVault(vault.vault_type); setAdjustAmount(""); setAdjustReason(""); }}
                      className="text-[9px] text-slate-500 hover:text-slate-300 flex items-center gap-0.5 transition-colors"
                      data-testid={`adjust-vault-${vault.vault_type}`}>
                      <PenLine className="w-2.5 h-2.5" /> Adjust
                    </button>
                    <span className="text-slate-700 text-[8px]">|</span>
                    <button onClick={() => openLedger(vault.vault_type)}
                      className="text-[9px] text-slate-500 hover:text-slate-300 flex items-center gap-0.5 transition-colors"
                      data-testid={`ledger-vault-${vault.vault_type}`}>
                      <History className="w-2.5 h-2.5" /> Ledger
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {/* Vault Explanations (when expanded) */}
            {showVaultDetail && r.vault_explanations && (
              <div className="mt-5 pt-5 border-t border-slate-800/30 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {vaultOrder.map(vt => {
                  const ve = r.vault_explanations[vt];
                  if (!ve) return null;
                  return (
                    <div key={vt} className="rounded-lg bg-slate-800/20 border border-slate-700/20 p-3" data-testid={`vault-explain-${vt}`}>
                      <div className="text-xs font-semibold text-slate-300 mb-1">{ve.label}</div>
                      <p className="text-[11px] text-slate-400 leading-relaxed">{ve.meaning}</p>
                      <p className="text-[10px] text-slate-500 mt-1.5 italic">{ve.implication}</p>
                      {ve.consequence && (
                        <p className="text-[10px] text-red-400/80 mt-1 font-medium">{ve.consequence}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </section>


      {/* ═══════════════════════════════════════════════════════
          SECTION 3.5 — SENTINEL AI INSIGHTS
          Deterministic numbers + LLM-written one-liners. Up to 5 cards,
          severity-ordered, dismissible per session.
          ═══════════════════════════════════════════════════════ */}
      <SentinelScoreStrip />
      <SentinelInsightsPanel />


      {/* ═══════════════════════════════════════════════════════
          SECTION 3.6 — MULTI-CURRENCY VAULT BREAKDOWN
          Auto-renders only when 2+ currencies are present on the
          ledger. Wholesale Wise traders need EUR / USD / GBP shown
          side-by-side without FX conversion.
          ═══════════════════════════════════════════════════════ */}
      <MultiCurrencyVaultStrip />

      {/* ═══════════════════════════════════════════════════════
          SECTION 3.7 — ADDITIONAL VAULTS IN DEVELOPMENT
          Surfaces the 4 non-pilot vaults (Credit Facility, Loan,
          Reserve, Strategic) as a secondary strip. Fed by the
          canonical /api/vaults/registry. Never auto-allocates.
          ═══════════════════════════════════════════════════════ */}
      <AdditionalVaultsPanel
        balancesByKey={(data?.vault_balances || {})}
        formatMoney={formatMoney}
      />


      {/* ═══════════════════════════════════════════════════════
          SECTION 4 — AI WARNINGS & BEHAVIOUR
          ═══════════════════════════════════════════════════════ */}
      <section data-testid="ai-warnings-section" data-tour="ai-warnings">
        <h2 className="font-barlow font-semibold text-xs text-slate-500 uppercase tracking-[0.2em] mb-3">AI Warnings & Behaviour</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Warnings Column */}
          <div className="lg:col-span-2 space-y-2">
            {/* Risk Horizon — time-based */}
            {r.risk_horizon?.length > 0 && (
              <div className="rounded-lg border border-red-500/20 bg-red-500/[0.03] p-3.5 space-y-2 mb-2" data-testid="risk-horizon">
                <div className="text-[10px] text-red-400 uppercase tracking-[0.15em] font-semibold flex items-center gap-1.5">
                  <Activity className="w-3 h-3" /> Risk Horizon
                </div>
                {r.risk_horizon.map((rh, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <span className="text-red-500/60 mt-0.5 shrink-0">&#8226;</span>
                    <span className="text-red-400/90 leading-relaxed">{rh}</span>
                  </div>
                ))}
              </div>
            )}
            {r.warnings?.length > 0 ? r.warnings.map((w, i) => {
              const s = SEVERITY_STYLES[w.severity] || SEVERITY_STYLES.medium;
              return (
                <div key={i} className={`rounded-lg border ${s.border} ${s.bg} p-3.5 flex items-start gap-3`} data-testid={`warning-${i}`}>
                  <AlertTriangle className={`w-4 h-4 ${s.text} mt-0.5 shrink-0`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <Badge className={`text-[8px] border-none ${s.badge}`}>{w.severity?.toUpperCase()}</Badge>
                      <span className="text-[9px] text-slate-600 uppercase tracking-wider">{w.type?.replace(/_/g, " ")}</span>
                    </div>
                    <p className={`text-sm ${s.text} leading-relaxed`}>{w.message}</p>
                  </div>
                </div>
              );
            }) : (
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] p-4 flex items-center gap-3">
                <Shield className="w-5 h-5 text-emerald-400" />
                <p className="text-sm text-emerald-400">No critical warnings. Your financial structure appears healthy.</p>
              </div>
            )}
          </div>

          {/* Behaviour Score Column */}
          <div className="rounded-xl bg-slate-900/60 border border-slate-800/50 p-5" data-testid="behaviour-card">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Financial Discipline</span>
              <Badge className={`text-[10px] font-mono px-2 py-0.5 ${
                r.behaviour_state === "STRATEGIC" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" :
                r.behaviour_state === "EMOTIONAL" ? "bg-amber-500/15 text-amber-400 border-amber-500/30" :
                "bg-red-500/15 text-red-400 border-red-500/30"
              } border`}>
                {r.behaviour_state || behaviour_state?.collapsed_state || "STRATEGIC"}
              </Badge>
            </div>
            <div className="flex justify-center mb-4">
              <GaugeMeter score={r.behaviour_score || probity_score || 75} label="Discipline Score" size={110} />
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed text-center">{r.behaviour_description || "Financial behaviour is being monitored."}</p>
            {coaching_preview?.[0] && (
              <div className="mt-3 p-2.5 rounded-lg bg-slate-800/40 border border-slate-700/30">
                <p className="text-[10px] text-slate-500 leading-relaxed line-clamp-2">{coaching_preview[0].content}</p>
              </div>
            )}
          </div>
        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════
          SECTION 5 — ACTION LAYER
          ═══════════════════════════════════════════════════════ */}
      <section data-testid="action-layer" data-tour="action-layer">
        <h2 className="font-barlow font-semibold text-xs text-slate-500 uppercase tracking-[0.2em] mb-3">Corrective Actions</h2>
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={() => navigate("/dashboard/data-pool")} className="bg-cyan-600 hover:bg-cyan-500 text-white gap-2" data-testid="action-diagnostic">
            <BrainCircuit className="w-4 h-4" /> Re-run AI Diagnostic
          </Button>
          <Button onClick={() => navigate("/dashboard/data-pool")} variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 gap-2" data-testid="action-review">
            <Search className="w-4 h-4" /> Review Unallocated Funds
          </Button>
          {(pressureLevel === "CRITICAL" || pressureLevel === "HIGH_RISK") && (
            <Button onClick={() => navigate("/dashboard/data-pool")} className="bg-red-600 hover:bg-red-500 text-white gap-2" data-testid="action-fix-imbalance">
              <Zap className="w-4 h-4" /> Fix Financial Imbalance
            </Button>
          )}
          <Button onClick={() => navigate("/dashboard/obligations")} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 gap-2" data-testid="action-resolve">
            <Shield className="w-4 h-4" /> Resolve Obligations Now
          </Button>
          <Button onClick={() => navigate("/dashboard/obligations")} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 gap-2" data-testid="action-stabilise">
            <Target className="w-4 h-4" /> Stabilise Cash Position
          </Button>
          <Button onClick={loadData} variant="ghost" className="text-slate-500 hover:text-slate-300 gap-2" data-testid="action-refresh">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════
          SUPPORTING SECTIONS (from original dashboard)
          ═══════════════════════════════════════════════════════ */}
      <PoolWidget />

      {/* ═══════════════════════════════════════════════════════
          FINANCIAL TRUTH REPORT — Generate & View
          ═══════════════════════════════════════════════════════ */}
      <Card className="bg-slate-900/40 border-slate-800/50" data-testid="truth-report-section">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-barlow font-semibold text-slate-200 tracking-wide flex items-center gap-2">
                <FileText className="w-4 h-4 text-amber-400" />
                Financial Truth Report
              </h3>
              <p className="text-[10px] text-slate-500 mt-0.5">Generate a comprehensive report from ledger-backed truth data</p>
            </div>
            <Button onClick={generateTruthReport} disabled={reportLoading}
              className="bg-amber-600 hover:bg-amber-500 text-slate-900 font-semibold text-xs gap-1.5"
              data-testid="generate-truth-report-btn">
              {reportLoading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating...</> : <><FileText className="w-3.5 h-3.5" /> Generate Report</>}
            </Button>
          </div>

          {truthReport && (
            <div className="space-y-4 border-t border-slate-800/30 pt-4">
              {/* Executive Summary */}
              <div className="p-4 rounded-xl border border-amber-500/15 bg-amber-500/[0.03]">
                <div className="text-[10px] text-amber-400 uppercase tracking-wider font-semibold mb-2">Executive Summary</div>
                <div className="text-sm text-slate-200 font-semibold">{truthReport.executive_summary?.headline}</div>
                <div className="text-xs text-slate-400 mt-1">{truthReport.executive_summary?.subtext}</div>
                <div className="flex items-center gap-4 mt-3">
                  <Badge className={`text-[9px] border-none ${
                    truthReport.executive_summary?.pressure_level === "CRITICAL" ? "bg-red-500/15 text-red-400" :
                    truthReport.executive_summary?.pressure_level === "HIGH_RISK" ? "bg-amber-500/15 text-amber-400" :
                    "bg-emerald-500/15 text-emerald-400"
                  }`}>{truthReport.executive_summary?.pressure_level}</Badge>
                  <span className="text-[10px] text-slate-500">
                    Owned: {truthReport.executive_summary?.owned_percentage}% | Obligated: {truthReport.executive_summary?.obligated_percentage}%
                  </span>
                </div>
              </div>

              {/* Data Integrity */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  { label: "Total Records", value: truthReport.data_integrity?.total_records, color: "text-slate-300" },
                  { label: "Committed", value: truthReport.data_integrity?.committed_records, color: "text-emerald-400" },
                  { label: "In Review", value: truthReport.data_integrity?.records_in_review, color: "text-amber-400" },
                  { label: "Failed", value: truthReport.data_integrity?.failed_records, color: "text-red-400" },
                  { label: "Edited", value: truthReport.data_integrity?.manually_edited_records, color: "text-orange-400" },
                ].map((stat) => (
                  <div key={stat.label} className="p-3 rounded-lg bg-slate-800/20 border border-slate-700/20 text-center">
                    <div className={`text-lg font-mono font-bold ${stat.color}`}>{stat.value ?? 0}</div>
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Recommended Actions */}
              {truthReport.recommended_actions?.length > 0 && (
                <div className="p-3 rounded-lg border border-cyan-500/15 bg-cyan-500/[0.02]">
                  <div className="text-[10px] text-cyan-400 uppercase tracking-wider font-semibold mb-2">Recommended Actions</div>
                  {truthReport.recommended_actions.map((action, i) => (
                    <div key={i} className="text-xs text-slate-400 flex items-start gap-2 mb-1">
                      <span className="text-cyan-400 mt-0.5 shrink-0">{i + 1}.</span>
                      <span>{action}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Download buttons */}
              <div className="flex items-center gap-3">
                <Button size="sm" variant="outline" onClick={() => downloadReport("json")}
                  className="border-slate-700 text-slate-400 hover:text-slate-200 text-xs gap-1" data-testid="download-report-json">
                  <Download className="w-3 h-3" /> JSON
                </Button>
                <Button size="sm" variant="outline" onClick={() => downloadReport("pdf")}
                  className="border-slate-700 text-slate-400 hover:text-slate-200 text-xs gap-1" data-testid="download-report-pdf">
                  <Download className="w-3 h-3" /> PDF
                </Button>
                <span className="text-[9px] text-slate-600">
                  Report ID: {truthReport.report_id?.substring(0, 8)}... | Generated: {new Date(truthReport.generated_at).toLocaleString("en-GB")}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Obligations Preview */}
      {obligations_summary?.length > 0 && (
        <Card className="bg-slate-900/40 border-slate-800/50 hover:border-slate-700/50 transition-colors cursor-pointer" onClick={() => navigate("/dashboard/obligations")} data-testid="obligations-preview-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-500 uppercase tracking-[0.15em] font-semibold">Upcoming Obligations</span>
              <span className="text-xs text-slate-600">{obligations_summary.length} due</span>
            </div>
            <div className="space-y-2">
              {obligations_summary.slice(0, 4).map((obl, idx) => (
                <div
                  key={`${obl.id || "obl"}-${obl.due_date || ""}-${obl.amount || idx}`}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge className={`text-[8px] border-none ${
                      obl.priority === "CRITICAL" ? "bg-red-500/15 text-red-400" :
                      obl.priority === "HIGH" ? "bg-amber-500/15 text-amber-400" :
                      "bg-slate-500/15 text-slate-400"
                    }`}>{obl.priority}</Badge>
                    <span className="text-xs text-slate-300 truncate">{obl.claimant}</span>
                  </div>
                  <div className="text-right shrink-0 ml-2">
                    <div className="text-xs font-mono tabular text-slate-200">{formatMoney(obl.amount)}</div>
                    <div className="text-[10px] text-slate-600">{obl.due_date}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Explain Panel */}
      <ExplainPanel open={explainOpen} onClose={() => setExplainOpen(false)} data={explainData} />

      {/* Onboarding — backend-state-driven, never blocks dashboard */}
      <OnboardingOverlay
        open={tourOpen}
        onClose={(payload) => {
          setTourOpen(false);
          onboarding.close(payload || {});
        }}
      />

      {/* ═══════════════════════════════════════════════════════
          MANUAL VAULT ADJUSTMENT MODAL
          ═══════════════════════════════════════════════════════ */}
      {vaultDetailType && (
        <VaultDetailDrawer
          vaultType={vaultDetailType}
          onClose={() => setVaultDetailType(null)}
        />
      )}

      <Dialog open={!!adjustVault} onOpenChange={() => setAdjustVault(null)}>
        <DialogContent className="bg-[#0B1120] border-slate-800 max-w-md" data-testid="adjust-vault-modal">
          <DialogHeader>
            <DialogTitle className="font-barlow text-slate-50 flex items-center gap-2">
              <PenLine className="w-5 h-5 text-cyan-400" /> Manual Vault Adjustment
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Use record editing first when a specific imported item is wrong. Use manual vault adjustment only when a direct vault-level correction is required.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-amber-500/[0.04] border border-amber-500/15 text-xs text-amber-400 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>This is a last-mile correction. Prefer editing individual records in the Data Pool for better traceability.</span>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Vault</Label>
              <Select value={adjustVault || ""} onValueChange={setAdjustVault}>
                <SelectTrigger className="bg-slate-900/50 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {vaultOrder.map(vt => (
                    <SelectItem key={vt} value={vt}>{vt.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Amount (positive = credit, negative = debit)</Label>
              <Input type="number" value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)}
                placeholder="e.g. 5000 or -2500"
                className="bg-slate-900/50 border-slate-700 mt-1 font-mono" data-testid="adjust-amount" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Reason (required)</Label>
              <Textarea value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)}
                placeholder="Why is this vault-level adjustment needed?"
                className="bg-slate-900/50 border-slate-700 mt-1 min-h-[60px] text-xs" data-testid="adjust-reason" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAdjustVault(null)} className="border-slate-700">Cancel</Button>
            <Button onClick={handleManualAdjust} disabled={adjusting || !adjustReason.trim() || !adjustAmount}
              className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="confirm-adjust-btn">
              {adjusting ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Applying...</> : "Apply Adjustment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════
          VAULT LEDGER HISTORY MODAL
          ═══════════════════════════════════════════════════════ */}
      <Dialog open={!!ledgerVault} onOpenChange={() => setLedgerVault(null)}>
        <DialogContent className="bg-[#0B1120] border-slate-800 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="vault-ledger-modal">
          <DialogHeader>
            <DialogTitle className="font-barlow text-slate-50 flex items-center gap-2">
              <Layers className="w-5 h-5 text-cyan-400" /> Vault Ledger — {(ledgerVault || "").replace(/_/g, " ")}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Full audit trail of all changes to this vault. Every entry is traceable to a committed pool item or manual adjustment.
            </DialogDescription>
          </DialogHeader>
          {ledgerLoading && (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
          )}
          {!ledgerLoading && ledgerEntries.length === 0 && (
            <div className="py-8 text-center text-sm text-slate-500">No ledger entries yet for this vault.</div>
          )}
          {!ledgerLoading && ledgerEntries.length > 0 && (
            <div className="space-y-2">
              {ledgerEntries.map((entry, idx) => (
                <div
                  key={`${entry.idempotency_key || entry.id || "ledger"}-${entry.created_at || idx}`}
                  className={`flex items-start justify-between p-3 rounded-lg border ${
                  entry.entry_type === "manual_adjustment" ? "border-amber-500/15 bg-amber-500/[0.03]" :
                  entry.entry_type === "ai_commit" ? "border-cyan-500/10 bg-cyan-500/[0.02]" :
                  "border-slate-700/30 bg-slate-800/20"
                }`} data-testid={`ledger-entry-${entry.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`text-[8px] border-none ${
                        entry.entry_type === "ai_commit" ? "bg-cyan-500/15 text-cyan-400" :
                        entry.entry_type === "manual_adjustment" ? "bg-amber-500/15 text-amber-400" :
                        "bg-slate-500/15 text-slate-400"
                      }`}>{entry.entry_type?.replace(/_/g, " ").toUpperCase()}</Badge>
                      <span className="text-[10px] text-slate-600">{new Date(entry.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                    <div className="text-xs text-slate-400 truncate">{entry.reason}</div>
                    {entry.source_item_id && (
                      <div className="text-[10px] text-slate-600 mt-0.5">Source: {entry.source_item_id.substring(0, 8)}...</div>
                    )}
                  </div>
                  <div className={`font-mono font-bold text-sm shrink-0 ml-3 ${entry.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {entry.amount >= 0 ? "+" : ""}{entry.amount?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLedgerVault(null)} className="border-slate-700">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
